import React, { useEffect, useRef } from 'react';
import { createEditor, createToolbar, IDomEditor, IEditorConfig } from '@wangeditor/editor';
import { message } from 'antd';
import { getToken } from '../utils/request';
import '@wangeditor/editor/dist/css/style.css';

interface RichTextEditorProps {
  value?: string;
  onChange?: (v: string) => void;
  height?: number;
}

// 图片上传走 admin 专用 COS 接口（与商品主图一致），仅支持 jpg/png/webp/gif、≤5MB
const UPLOAD_URL = `${process.env.REACT_APP_API_BASE || '/api'}/upload/admin-image`;

/**
 * 图文详情富文本编辑器（wangEditor，直接产出 HTML）
 * - 所见即所得，支持标题 / 加粗 / 列表 / 表格（含合并单元格）/ 图片上传
 * - 存的就是 HTML，与小程序 <RichText nodes={detail}> 渲染同源，不再有 Markdown 往返丢格式的问题
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, height = 420 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<IDomEditor | null>(null);
  const valueRef = useRef(value || '');
  // 最近一次由编辑器抛出的 HTML，用于区分「外部回填」与「自身输入」，避免 setHtml 打断光标
  const lastEmitRef = useRef(value || '');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!containerRef.current || !toolbarRef.current) return;

    const editorConfig: Partial<IEditorConfig> = {
      placeholder: '直接输入即可，支持标题、加粗、列表、表格；图片点工具栏上传或直接粘贴',
      MENU_CONF: {
        uploadImage: {
          async customUpload(file: File, insertFn: (url: string, alt: string, href: string) => void) {
            if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) {
              message.error(`${file.name}：仅支持 jpg / png / webp / gif 图片`);
              return;
            }
            if (file.size > 5 * 1024 * 1024) {
              message.error(`${file.name} 超过 5MB`);
              return;
            }
            const fd = new FormData();
            fd.append('file', file);
            try {
              const res = await fetch(UPLOAD_URL, {
                method: 'POST',
                headers: { Authorization: `Bearer ${getToken()}` },
                body: fd,
              });
              const json = await res.json();
              const url = json?.data?.url;
              if (!url) throw new Error(json?.message || '上传失败');
              insertFn(url, file.name.replace(/\.[^.]+$/, ''), url);
            } catch (e: any) {
              message.error(e?.message || `${file.name} 上传失败`);
            }
          },
        },
      },
      onChange(editor) {
        const html = editor.getHtml();
        lastEmitRef.current = html;
        onChangeRef.current?.(html);
      },
    };

    const editor = createEditor({
      selector: containerRef.current,
      html: valueRef.current || '',
      config: editorConfig,
      mode: 'default',
    });
    // 默认工具栏已含「图片上传」「表格」等；这里只去掉与商品详情无关的视频组
    createToolbar({
      editor,
      selector: toolbarRef.current,
      config: { excludeKeys: ['group-video'] },
      mode: 'default',
    });
    editorRef.current = editor;

    return () => {
      try { editor.destroy(); } catch { /* ignore */ }
      editorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 外部值变化（切换编辑的商品 / resetFields / 新建清空）时回填，自身输入不回填
  useEffect(() => {
    const editor = editorRef.current;
    const v = value || '';
    if (editor && v !== lastEmitRef.current) {
      lastEmitRef.current = v;
      if (v) editor.setHtml(v);
      else editor.clear();
    }
  }, [value]);

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 8 }}>
      <div ref={toolbarRef} style={{ borderBottom: '1px solid #eee' }} />
      <div ref={containerRef} style={{ height, overflowY: 'auto' }} />
    </div>
  );
};

export default RichTextEditor;
